import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import ActionButtons from "../../components/common/ActionButtons";
import {
  useGetTerminalsQuery,
  useCreateTerminalMutation,
  useUpdateTerminalMutation,
  useDeleteTerminalMutation,
} from "../../store/api";

const PROVIDERS = ["BRIDGE", "STRIPE", "SQUARE", "ADYEN"];
const MODELS = ["PAX", "VERIFONE", "INGENICO", "CLOVER"];

const Terminals = () => {
  const { data, isLoading, isError, error } = useGetTerminalsQuery();
  const [createTerminal, createState] = useCreateTerminalMutation();
  const [updateTerminal, updateState] = useUpdateTerminalMutation();
  const [deleteTerminal] = useDeleteTerminalMutation();

  const [form, setForm] = useState({
    terminalName: "",
    provider: "",
    model: "",
    serialNo: "",
    pairingCode: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    terminalId: null,
    terminalName: "",
  });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const terminals = Array.isArray(data) ? data : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        terminalName: form.terminalName,
        provider: form.provider,
        model: form.model,
        serialNo: form.serialNo,
        pairingCode: form.pairingCode,
        isActive: form.isActive,
      };
      if (editingId) {
        await updateTerminal({
          terminalId: editingId,
          payload,
        }).unwrap();
        setEditingId(null);
      } else {
        await createTerminal(payload).unwrap();
      }
      setForm({
        terminalName: "",
        provider: "",
        model: "",
        serialNo: "",
        pairingCode: "",
        isActive: true,
      });
      setShowForm(false);
    } catch (err) {
      // error handled by UI
    }
  };

  const handleEdit = (terminal) => {
    setEditingId(terminal.terminalId);
    setForm({
      terminalName: terminal.terminalName || "",
      provider: terminal.provider || "",
      model: terminal.model || "",
      serialNo: terminal.serialNo || "",
      pairingCode: terminal.pairingCode || "",
      isActive: terminal.isActive !== undefined ? terminal.isActive : true,
    });
    setShowForm(true);
  };

  const handleDelete = (terminalId) => {
    const terminal = terminals.find((t) => t.terminalId === terminalId);
    setDeleteModal({
      isOpen: true,
      terminalId,
      terminalName: terminal?.terminalName || "this terminal",
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteTerminal(deleteModal.terminalId).unwrap();
      setDeleteModal({ isOpen: false, terminalId: null, terminalName: "" });
    } catch (err) {
      setDeleteModal({ isOpen: false, terminalId: null, terminalName: "" });
      const errorMessage =
        err?.data?.detail || err?.data?.message || "Failed to delete terminal.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm({
      terminalName: "",
      provider: "",
      model: "",
      serialNo: "",
      pairingCode: "",
      isActive: true,
    });
    setShowForm(false);
  };

  const columns = [
    { key: "terminalName", label: "Terminal Name" },
    { key: "provider", label: "Provider" },
    { key: "model", label: "Model" },
    { key: "serialNo", label: "Serial Number" },
    { key: "pairingCode", label: "Pairing Code" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];

  const rows = terminals.map((terminal) => {
    return {
      id: terminal.terminalId,
      terminalName: terminal.terminalName,
      provider: terminal.provider || "—",
      model: terminal.model || "—",
      serialNo: terminal.serialNo || "—",
      pairingCode: terminal.pairingCode || "—",
      status: (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {terminal.isActive ? "Active" : "Inactive"}
        </span>
      ),
      actions: (
        <div className="flex gap-2">
          <button
            type="button"
            className="p-1 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition"
            onClick={() => handleEdit(terminal)}
            title="Edit"
            aria-label="Edit terminal"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            type="button"
            className="p-1 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded transition"
            onClick={() => handleDelete(terminal.terminalId)}
            title="Delete"
            aria-label="Delete terminal"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Terminals"
        subtitle="Manage payment terminals."
        actions={
          showForm ? (
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Back
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Add Terminal
            </Button>
          )
        }
      />

      {showForm && (
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingId ? "Edit Terminal" : "Add Terminal"}
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Terminal Name"
                name="terminalName"
                placeholder="QA Terminal 01"
                value={form.terminalName}
                onChange={handleChange}
                required
              />
              <Input
                label="Pairing Code"
                name="pairingCode"
                placeholder="TERM-001"
                value={form.pairingCode}
                onChange={handleChange}
                required
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Provider"
                name="provider"
                value={form.provider}
                onChange={handleChange}
                required
              >
                <option value="">Select provider</option>
                {PROVIDERS.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </Select>
              <Select
                label="Model"
                name="model"
                value={form.model}
                onChange={handleChange}
                required
              >
                <option value="">Select model</option>
                {MODELS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </Select>
            </div>

            <Input
              label="Serial Number"
              name="serialNo"
              placeholder="QA-SERIAL-001"
              value={form.serialNo}
              onChange={handleChange}
              required
            />

            <Checkbox
              label="Active"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
            />

            {(createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">
                Terminal {editingId ? "updated" : "created"} successfully.
              </Alert>
            )}
            {(createState.isError || updateState.isError) && (
              <Alert tone="error">
                {createState.error?.data?.detail ||
                  updateState.error?.data?.detail ||
                  "Failed to save terminal."}
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={createState.isLoading || updateState.isLoading}
              >
                {editingId ? "Update" : "Add"} Terminal
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!showForm && (
        <Card className="p-0">
          <div className="border-b border-slate-100 dark:border-slate-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                All Terminals
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {terminals.length} terminal{terminals.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <div className="px-6 py-4">
            {isLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading terminals...
              </p>
            )}
            {isError && (
              <Alert tone="error">
                {error?.data?.detail || "Failed to load terminals."}
              </Alert>
            )}
            {!isLoading && !isError && (
              <Table
                columns={columns}
                rows={rows}
                emptyText="No terminals yet."
              />
            )}
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, terminalId: null, terminalName: "" })
        }
        title="Delete Terminal"
        confirmText="Delete"
        confirmVariant="primary"
        onConfirm={confirmDelete}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.terminalName}</strong>?
        </p>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This action cannot be undone.
        </p>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default Terminals;
